import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Divider, Form, Input, List, Row, Switch, Tag, Typography } from "antd";
import { api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { PageHeader } from "../components/PageHeader";
import { useUiStore } from "../ui/theme";
import { AppearanceSettings } from "../workspace/AppearanceSettings";

interface Session {
  id: string;
  created_at: string;
  last_seen_at: string;
  ip: string | null;
  user_agent: string | null;
  current: boolean;
}

/** Own profile: password, devices (refresh sessions), theme and reset of saved table columns. */
export function ProfilePage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const loadMe = useAuthStore((s) => s.loadMe);
  const savePreferences = useAuthStore((s) => s.savePreferences);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  // ТЗ «Рабочие столы» 31.10.2026, 6.1: «Профиль → Оформление» also chooses the own background and button colour (UX04)
  const personal = useAuthStore((s) => s.option)("use_personal_workspace");
  const [form] = Form.useForm();

  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.get<Session[]>("/auth/sessions") });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sessions"] }),
    onError: (e) => message.error(errorMessage(e)),
  });
  const change = useMutation({
    mutationFn: (values: { current_password: string; new_password: string }) => api.post("/auth/change-password", values),
    onSuccess: () => {
      message.success("Пароль изменён");
      form.resetFields();
      loadMe().catch(() => undefined);
    },
    onError: (e) => message.error(errorMessage(e)),
  });

  const appearance = (
    <Card title="Оформление" size="small" id="appearance" data-testid="profile-appearance">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Switch
          checked={theme === "dark"}
          onChange={(on) => {
            setTheme(on ? "dark" : "light");
            savePreferences({ theme: on ? "dark" : "light" }).catch(() => undefined);
          }}
        />
        <span>Тёмная тема</span>
      </div>
      {/* ТЗ 1.2 раздел 4.2: a larger working text and taller controls as a personal setting, not a global change */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <Switch
          checked={density === "large"}
          data-testid="density-switch"
          onChange={(on) => {
            setDensity(on ? "large" : "default");
            savePreferences({ density: on ? "large" : "default" }).catch(() => undefined);
          }}
        />
        <span>Крупный интерфейс: текст 18 px, кнопки и поля 44 px</span>
      </div>
      <div style={{ marginTop: 12 }}>
        <Button onClick={() => savePreferences({ views: null }).then(() => message.success("Колонки списков сброшены")).catch((e) => message.error(errorMessage(e)))}>
          Сбросить сохранённые колонки списков
        </Button>
      </div>
      {personal && (
        <>
          <Divider style={{ margin: "20px 0" }} />
          <AppearanceSettings cabinet="staff" />
        </>
      )}
    </Card>
  );

  return (
    <div style={{ maxWidth: personal ? 1100 : 1000 }}>
      <PageHeader title={me?.user.full_name} subtitle="Пароль, устройства и оформление" />
      {me?.password_expired && <Alert type="warning" showIcon message="Нужно сменить пароль" description="Срок действия пароля истёк или администратор потребовал его смены." style={{ marginBottom: 16 }} />}
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title="Смена пароля" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" onFinish={(v) => change.mutate(v)}>
              <Form.Item name="current_password" label="Текущий пароль" rules={[{ required: true, message: "Введите текущий пароль" }]}>
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Form.Item name="new_password" label="Новый пароль" rules={[{ required: true, message: "Введите новый пароль" }]} extra="Не короче 8 символов, хотя бы одна цифра">
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item name="confirm" label="Повторите пароль" dependencies={["new_password"]} rules={[{ required: true, message: "Повторите пароль" }, ({ getFieldValue }) => ({ validator: (_, v) => (v === getFieldValue("new_password") ? Promise.resolve() : Promise.reject(new Error("Пароли не совпадают"))) })]}>
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={change.isPending}>
                Сменить пароль
              </Button>
            </Form>
          </Card>
          {personal ? null : appearance}
        </Col>
        <Col xs={24} md={12}>
          <Card title="Устройства и сеансы" size="small" extra={<Typography.Text type="secondary">выход на другом устройстве</Typography.Text>}>
            <List
              size="small"
              loading={sessions.isLoading}
              dataSource={sessions.data ?? []}
              renderItem={(s) => (
                <List.Item actions={s.current ? [] : [<Button key="x" size="small" danger onClick={() => revoke.mutate(s.id)}>Завершить</Button>]}>
                  <List.Item.Meta
                    title={
                      <span>
                        {s.user_agent?.slice(0, 60) || "Неизвестное устройство"} {s.current && <Tag color="blue">текущий</Tag>}
                      </span>
                    }
                    description={`вход ${new Date(s.created_at).toLocaleString("ru-RU")}, активность ${new Date(s.last_seen_at).toLocaleString("ru-RU")}${s.ip ? `, ${s.ip}` : ""}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
      {/* with the palette the card takes the whole width: eight swatches in a row and the sample under them */}
      {personal && <div style={{ marginTop: 16 }}>{appearance}</div>}
    </div>
  );
}
